import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  Clock,
  History,
  Loader2,
  MessageSquare,
  Pause,
  Play,
  RefreshCw,
  Repeat,
  Trash2,
  Zap,
} from 'lucide-react';
import {
  type PaRoutine,
  type PaRoutineRun,
  type PaRoutineRunDetail,
  type PaRoutineTrigger,
} from '@easyflow/shared';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { RoutinesDialogs } from './RoutinesDialogs';
import {
  decideRoutineRun,
  deleteRoutine,
  getRoutineRun,
  listRoutineRuns,
  listRoutines,
  setRoutineActive,
  startRoutine,
  updateRoutineSchedule,
} from './routineApi';
import {
  cronFromDraft,
  describeTrigger,
  draftFromRoutine,
  type ScheduleDraft,
} from './routineSchedule';
import {
  formatRoutineDateTime,
  RoutineStatusBadge,
} from './routinePresentation';

export interface RoutinesPanelProps {
  onOpenInChat: (routine?: PaRoutine) => void;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export const RoutinesPanel: React.FC<RoutinesPanelProps> = ({ onOpenInChat }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const ownerKey = user ? `${user.tenantSlug}:${user.id}` : '';
  const generationRef = useRef(0);
  const timersRef = useRef<Set<number>>(new Set());
  const [routines, setRoutines] = useState<PaRoutine[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [logRoutine, setLogRoutine] = useState<PaRoutine | null>(null);
  const [runs, setRuns] = useState<PaRoutineRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [runDetail, setRunDetail] = useState<PaRoutineRunDetail | null>(null);
  const [runDetailLoading, setRunDetailLoading] = useState(false);
  const [scheduleRoutine, setScheduleRoutine] = useState<PaRoutine | null>(null);
  const [scheduleDraft, setScheduleDraft] = useState<ScheduleDraft | null>(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PaRoutine | null>(null);
  const [deleteSending, setDeleteSending] = useState(false);
  const [confirmRun, setConfirmRun] = useState<{ routine: PaRoutine; run: PaRoutineRunDetail } | null>(null);
  const [confirmSending, setConfirmSending] = useState(false);

  const isCurrent = useCallback((generation: number) => generationRef.current === generation, []);

  const recoverPendingConfirmation = useCallback(async (items: PaRoutine[], generation: number) => {
    const candidates = await Promise.all(items.map(async routine => {
      try {
        const limit = 100;
        let offset = 0;
        while (isCurrent(generation)) {
          const list = await listRoutineRuns(routine.id, limit, offset, 'awaiting_confirmation');
          const pending = list.items.find(run =>
            run.status === 'awaiting_confirmation' && run.confirmation_status === 'pending',
          );
          if (pending) {
            const detail = await getRoutineRun(routine.id, pending.id);
            return { routine, run: detail };
          }
          offset += list.items.length;
          if (list.items.length === 0 || offset >= list.pagination.total) return null;
        }
        return null;
      } catch (error) {
        return error instanceof Error ? error : new Error('Recupero conferme non riuscito');
      }
    }));
    if (!isCurrent(generation)) return;
    if (candidates.some(candidate => candidate instanceof Error)) {
      setRecoveryError('Alcune conferme in sospeso non sono state recuperate. Riprova.');
    }
    const recovered = candidates
      .filter((candidate): candidate is { routine: PaRoutine; run: PaRoutineRunDetail } =>
        candidate !== null && !(candidate instanceof Error),
      )
      .sort((left, right) => right.run.started_at.localeCompare(left.run.started_at))[0];
    if (recovered) setConfirmRun(recovered);
  }, [isCurrent]);

  const load = useCallback(async (showLoading = true) => {
    const generation = generationRef.current;
    if (!ownerKey) {
      setRoutines([]);
      setLoading(false);
      setLoadError('Utente non disponibile');
      return;
    }
    if (showLoading) setLoading(true);
    setLoadError(null);
    setRecoveryError(null);
    try {
      const items = await listRoutines();
      if (!isCurrent(generation)) return;
      setRoutines(items);
      void recoverPendingConfirmation(items, generation).catch(() => undefined);
    } catch (error) {
      if (isCurrent(generation)) setLoadError(errorMessage(error, 'Impossibile caricare le routine'));
    } finally {
      if (isCurrent(generation)) setLoading(false);
    }
  }, [isCurrent, ownerKey, recoverPendingConfirmation]);

  useEffect(() => {
    const timers = timersRef.current;
    generationRef.current += 1;
    setRoutines([]);
    setConfirmRun(null);
    void load();
    return () => {
      generationRef.current += 1;
      for (const timer of timers) window.clearTimeout(timer);
      timers.clear();
    };
  }, [load, ownerKey]);

  const pollRun = useCallback((routine: PaRoutine, runId: string, attempt = 0) => {
    if (attempt > 90) return;
    const generation = generationRef.current;
    const timer = window.setTimeout(() => {
      timersRef.current.delete(timer);
      void getRoutineRun(routine.id, runId).then(detail => {
        if (!isCurrent(generation)) return;
        if (detail.status === 'awaiting_confirmation' && detail.confirmation_status === 'pending') {
          setConfirmRun({ routine, run: detail });
          return;
        }
        if (detail.status === 'running') {
          pollRun(routine, runId, attempt + 1);
          return;
        }
        void load(false);
        if (detail.status === 'completed') {
          toast({ title: 'Routine completata', description: `"${routine.title}" è stata eseguita con successo.` });
        } else if (detail.status === 'failed') {
          toast({ title: 'Routine fallita', description: detail.error_message ?? 'Esecuzione non riuscita', variant: 'destructive' });
        }
      }).catch(() => undefined);
    }, 2000);
    timersRef.current.add(timer);
  }, [isCurrent, load, toast]);

  const handlePlay = useCallback(async (routine: PaRoutine) => {
    const generation = generationRef.current;
    setBusyId(routine.id);
    try {
      const runId = await startRoutine(routine.id);
      if (!isCurrent(generation)) return;
      toast({ title: 'Routine avviata', description: `"${routine.title}" è in esecuzione.` });
      pollRun(routine, runId);
      void load(false);
    } catch (error) {
      if (isCurrent(generation)) {
        toast({ title: 'Errore', description: errorMessage(error, 'Avvio non riuscito'), variant: 'destructive' });
      }
    } finally {
      if (isCurrent(generation)) setBusyId(null);
    }
  }, [isCurrent, load, pollRun, toast]);

  const handleToggleActive = useCallback(async (routine: PaRoutine) => {
    const generation = generationRef.current;
    setBusyId(routine.id);
    try {
      await setRoutineActive(routine.id, !routine.is_active);
      if (isCurrent(generation)) await load(false);
    } catch (error) {
      if (isCurrent(generation)) {
        toast({ title: 'Errore', description: errorMessage(error, 'Operazione non riuscita'), variant: 'destructive' });
      }
    } finally {
      if (isCurrent(generation)) setBusyId(null);
    }
  }, [isCurrent, load, toast]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const generation = generationRef.current;
    setDeleteSending(true);
    try {
      await deleteRoutine(deleteTarget.id);
      if (!isCurrent(generation)) return;
      toast({ title: 'Routine eliminata', description: `"${deleteTarget.title}" è stata eliminata.` });
      setDeleteTarget(null);
      await load(false);
    } catch (error) {
      if (isCurrent(generation)) {
        toast({ title: 'Errore', description: errorMessage(error, 'Eliminazione non riuscita'), variant: 'destructive' });
      }
    } finally {
      if (isCurrent(generation)) setDeleteSending(false);
    }
  }, [deleteTarget, isCurrent, load, toast]);

  const openLogs = useCallback(async (routine: PaRoutine) => {
    const generation = generationRef.current;
    setLogRoutine(routine);
    setRunDetail(null);
    setRuns([]);
    setRunsError(null);
    setRunsLoading(true);
    try {
      const result = await listRoutineRuns(routine.id);
      if (isCurrent(generation)) setRuns(result.items);
    } catch (error) {
      if (isCurrent(generation)) setRunsError(errorMessage(error, 'Impossibile caricare il registro'));
    } finally {
      if (isCurrent(generation)) setRunsLoading(false);
    }
  }, [isCurrent]);

  const openRunDetail = useCallback(async (routine: PaRoutine, run: PaRoutineRun) => {
    const generation = generationRef.current;
    setRunDetailLoading(true);
    setRunsError(null);
    try {
      const detail = await getRoutineRun(routine.id, run.id);
      if (!isCurrent(generation)) return;
      setRunDetail(detail);
      if (detail.status === 'awaiting_confirmation' && detail.confirmation_status === 'pending') {
        setConfirmRun({ routine, run: detail });
      }
    } catch (error) {
      if (isCurrent(generation)) setRunsError(errorMessage(error, "Impossibile caricare l'esecuzione"));
    } finally {
      if (isCurrent(generation)) setRunDetailLoading(false);
    }
  }, [isCurrent]);

  const handleScheduleSave = useCallback(async () => {
    if (!scheduleRoutine || !scheduleDraft) return;
    const generation = generationRef.current;
    let trigger: PaRoutineTrigger;
    if (scheduleDraft.kind === 'event') {
      trigger = { kind: 'event', event_type: scheduleDraft.eventType };
    } else {
      const cron = cronFromDraft(scheduleDraft);
      if (!cron) {
        toast({ title: 'Pianificazione non valida', description: 'Controlla orario e ricorrenza.', variant: 'destructive' });
        return;
      }
      trigger = { kind: 'time', cron_expression: cron, timezone: 'Europe/Rome' };
    }
    setScheduleSaving(true);
    try {
      await updateRoutineSchedule(scheduleRoutine.id, { trigger });
      if (!isCurrent(generation)) return;
      toast({ title: 'Pianificazione aggiornata', description: `"${scheduleRoutine.title}" è stata aggiornata.` });
      setScheduleRoutine(null);
      setScheduleDraft(null);
      await load(false);
    } catch (error) {
      if (isCurrent(generation)) {
        toast({ title: 'Errore', description: errorMessage(error, 'Salvataggio non riuscito'), variant: 'destructive' });
      }
    } finally {
      if (isCurrent(generation)) setScheduleSaving(false);
    }
  }, [isCurrent, load, scheduleDraft, scheduleRoutine, toast]);

  const handleConfirmDecision = useCallback(async (decision: 'approve' | 'reject') => {
    if (!confirmRun) return;
    const generation = generationRef.current;
    setConfirmSending(true);
    try {
      await decideRoutineRun(confirmRun.routine.id, confirmRun.run.id, { decision });
      if (!isCurrent(generation)) return;
      toast({
        title: decision === 'approve' ? 'Confermato' : 'Annullato',
        description: decision === 'approve'
          ? 'La routine prosegue con le azioni confermate.'
          : "L'esecuzione è stata annullata.",
      });
      const selected = confirmRun;
      setConfirmRun(null);
      if (decision === 'approve') pollRun(selected.routine, selected.run.id);
      void load(false);
    } catch (error) {
      if (isCurrent(generation)) {
        toast({ title: 'Errore', description: errorMessage(error, 'Operazione non riuscita'), variant: 'destructive' });
      }
    } finally {
      if (isCurrent(generation)) setConfirmSending(false);
    }
  }, [confirmRun, isCurrent, load, pollRun, toast]);

  const sorted = useMemo(
    () => [...routines].sort((left, right) => right.created_at.localeCompare(left.created_at)),
    [routines],
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2">
        <div>
          <div className="text-[11px] font-semibold uppercase text-muted-foreground">Routine</div>
          <div className="text-[10px] text-muted-foreground">Automazioni personali</div>
        </div>
        <Button size="sm" className="h-7 px-2 text-xs" onClick={() => onOpenInChat()}>
          <MessageSquare className="mr-1 h-3.5 w-3.5" />
          Nuova
        </Button>
      </div>
      {recoveryError && (
        <div role="alert" className="flex items-center gap-2 border-b border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1">{recoveryError}</span>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => void load(false)}>Riprova</Button>
        </div>
      )}
      <ScrollArea className="min-h-0 flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : loadError ? (
          <div className="space-y-3 px-4 py-8 text-center">
            <p className="text-xs text-destructive">{loadError}</p>
            <Button size="sm" variant="outline" onClick={() => void load()}>
              <RefreshCw className="mr-1 h-3.5 w-3.5" />Riprova
            </Button>
          </div>
        ) : sorted.length === 0 ? (
          <div className="space-y-3 px-4 py-10 text-center">
            <Repeat className="mx-auto h-8 w-8 text-muted-foreground/50" />
            <p className="text-xs text-muted-foreground">Non hai ancora routine personali.</p>
            <Button size="sm" variant="outline" onClick={() => onOpenInChat()}>Chiedi all'assistente</Button>
          </div>
        ) : (
          <ul className="space-y-2 p-2">
            {sorted.map(routine => {
              const needsProof = routine.is_dangerous && !routine.proven_at;
              return (
                <li key={routine.id} className="rounded-lg border border-border bg-background p-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 text-sm font-medium leading-snug">{routine.title}</div>
                    <Badge className={routine.is_active ? 'shrink-0 border-0 bg-success/15 text-success' : 'shrink-0 border-0 bg-muted text-muted-foreground'}>
                      {routine.is_active ? 'Attiva' : 'In pausa'}
                    </Badge>
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{routine.description}</p>
                  <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                    <div className="flex items-start gap-1.5">
                      {routine.trigger_kind === 'event' ? <Zap className="mt-0.5 h-3 w-3 shrink-0" /> : <Clock className="mt-0.5 h-3 w-3 shrink-0" />}
                      <span>{describeTrigger(routine)}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <History className="h-3 w-3 shrink-0" />
                      <span>{formatRoutineDateTime(routine.last_run_at)}</span>
                      {routine.last_run_status && <RoutineStatusBadge status={routine.last_run_status} />}
                    </div>
                    {needsProof && (
                      <div className="flex items-start gap-1.5 text-warning">
                        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                        <span>Richiede una prova manuale prima dell'avvio automatico</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <Button size="sm" className="h-7 px-2 text-xs" disabled={busyId === routine.id} onClick={() => void handlePlay(routine)}>
                      {busyId === routine.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                      <span className="ml-1">Esegui</span>
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={busyId === routine.id} title={routine.is_active ? 'Metti in pausa' : 'Riattiva'} onClick={() => void handleToggleActive(routine)}>
                      {routine.is_active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" title="Cambia pianificazione" onClick={() => { setScheduleRoutine(routine); setScheduleDraft(draftFromRoutine(routine)); }}>
                      <CalendarClock className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" title="Registro esecuzioni" onClick={() => void openLogs(routine)}>
                      <History className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" title="Modifica in chat" onClick={() => onOpenInChat(routine)}>
                      <MessageSquare className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-destructive hover:text-destructive" title="Elimina" onClick={() => setDeleteTarget(routine)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </ScrollArea>

      <RoutinesDialogs
        logRoutine={logRoutine}
        runs={runs}
        runsLoading={runsLoading}
        runsError={runsError}
        runDetail={runDetail}
        runDetailLoading={runDetailLoading}
        onCloseLogs={() => { setLogRoutine(null); setRunDetail(null); }}
        onClearRunDetail={() => setRunDetail(null)}
        onOpenRunDetail={(routine, run) => { void openRunDetail(routine, run); }}
        scheduleRoutine={scheduleRoutine}
        scheduleDraft={scheduleDraft}
        scheduleSaving={scheduleSaving}
        onScheduleDraftChange={setScheduleDraft}
        onCloseSchedule={() => { setScheduleRoutine(null); setScheduleDraft(null); }}
        onScheduleSave={() => { void handleScheduleSave(); }}
        deleteTarget={deleteTarget}
        deleteSending={deleteSending}
        onCloseDelete={() => setDeleteTarget(null)}
        onDelete={() => { void handleDelete(); }}
        confirmRun={confirmRun}
        confirmSending={confirmSending}
        onCloseConfirm={() => setConfirmRun(null)}
        onConfirmDecision={decision => { void handleConfirmDecision(decision); }}
      />
    </div>
  );
};